/**
 * SchedulesPage Component
 * Main page for Schedule Builder feature (Feature 004).
 */
import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  SxProps,
  Theme,
} from '@mui/material';
import {
  Add as AddIcon,
  ViewList as TemplateIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDevice } from '../../contexts/DeviceContext';
import { ScheduleList, CreateScheduleDialog, ScheduleEditor } from '../schedules';
import { SendNowProps } from '../schedules/ScheduleCard';
import { Schedule } from '../../types/schedule';
import { SENDABLE_SCHEDULE_TYPES, isSentScheduleActive } from '../../types/sentSchedule';
import { scheduleService } from '../../services/scheduleService';
import { useTabletNow } from '../../hooks/useTabletNow';
import { useSendScheduleFlow } from '../useSendScheduleFlow';
import { OnTabletStrip } from '../OnTabletStrip';

type ViewMode = 'list' | 'editor';

// BA-105: a phone tap target below 600 px; the desktop button keeps its height
const headerButtonSx: SxProps<Theme> = (theme) => ({
  minHeight: 44,
  [theme.breakpoints.up('sm')]: { minHeight: 'auto' },
});

const SchedulesPage: React.FC = () => {
  const { t } = useTranslation();
  const { selectedDevice, deviceActions } = useDevice();
  const deviceId = selectedDevice?.id ?? '';
  const deviceName = selectedDevice?.name ?? '';
  const navigate = useNavigate();

  // BA-85: what is on the tablet now, and the Send now flow
  const tablet = useTabletNow(deviceId);
  const actionName = tablet.currentAction
    ? (deviceActions ?? []).find((a: { id: string }) => a.id === tablet.currentAction?.actionId)?.name
      ?? tablet.currentAction.actionId
    : '';
  const [sentSnack, setSentSnack] = useState(false);
  const flow = useSendScheduleFlow({
    deviceId,
    deviceName,
    sentSchedule: tablet.sentSchedule,
    currentAction: tablet.currentAction,
    actionName,
    tabletOffline: tablet.tabletOffline,
    onSent: () => setSentSnack(true),
  });

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);

  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState<Schedule | null>(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Handle schedule created
  const handleScheduleCreated = useCallback((scheduleId: string) => {
    setSelectedScheduleId(scheduleId);
    setViewMode('editor');
    setSnackbar({
      open: true,
      message: t('schedules.success.scheduleCreated'),
      severity: 'success',
    });
  }, [t]);

  // Handle edit schedule
  const handleEdit = useCallback((schedule: Schedule) => {
    setSelectedScheduleId(schedule.id);
    setViewMode('editor');
  }, []);

  // Handle duplicate schedule
  const handleDuplicate = useCallback(async (schedule: Schedule) => {
    try {
      await scheduleService.duplicateSchedule(deviceId, schedule.id);
      setSnackbar({
        open: true,
        message: t('schedules.success.scheduleDuplicated'),
        severity: 'success',
      });
    } catch (err) {
      console.error('Failed to duplicate schedule:', err);
      setSnackbar({
        open: true,
        message: t('schedules.errors.failedToDuplicateSchedule'),
        severity: 'error',
      });
    }
  }, [deviceId, t]);

  // Handle delete schedule
  const handleDelete = useCallback((schedule: Schedule) => {
    setScheduleToDelete(schedule);
    setDeleteDialogOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!scheduleToDelete) return;

    try {
      await scheduleService.deleteSchedule(deviceId, scheduleToDelete.id);
      setSnackbar({
        open: true,
        message: t('schedules.success.scheduleDeleted'),
        severity: 'success',
      });
    } catch (err) {
      console.error('Failed to delete schedule:', err);
      setSnackbar({
        open: true,
        message: t('schedules.errors.failedToDeleteSchedule'),
        severity: 'error',
      });
    }
    setScheduleToDelete(null);
    setDeleteDialogOpen(false);
  }, [scheduleToDelete, deviceId, t]);

  // Handle toggle active
  const handleToggleActive = useCallback(async (schedule: Schedule) => {
    try {
      await scheduleService.updateSchedule(deviceId, schedule.id, {
        isActive: !schedule.isActive,
      });
      setSnackbar({
        open: true,
        message: t('schedules.success.scheduleUpdated'),
        severity: 'success',
      });
    } catch (err) {
      console.error('Failed to toggle schedule active:', err);
      setSnackbar({
        open: true,
        message: t('schedules.errors.failedToUpdateSchedule'),
        severity: 'error',
      });
    }
  }, [deviceId, t]);

  // Handle back from editor
  const handleBackFromEditor = useCallback(() => {
    setViewMode('list');
    setSelectedScheduleId(null);
  }, []);

  if (!selectedDevice) return null;

  const canSend = selectedDevice.role !== 'viewer';
  // n2: until both listeners have answered, the page can't tell a send from a replace
  const sendBlocked = !tablet.ready || !tablet.online || !!flow.sendingId;
  const sendNowFor = (schedule: Schedule): SendNowProps | undefined => {
    if (!canSend || schedule.isTemplate || !SENDABLE_SCHEDULE_TYPES.includes(schedule.type)) {
      return undefined;
    }
    const error = flow.errors[schedule.id];
    return {
      onSend: () => flow.request({ scheduleId: schedule.id, name: schedule.name }),
      state: flow.sendingId === schedule.id ? 'sending' : sendBlocked ? 'disabled' : 'idle',
      error: error ? t(`sentSchedule.${error}`, { name: schedule.name }) : undefined,
      ariaLabel: t('schedules.sendNowAria', { name: schedule.name, device: deviceName }),
    };
  };
  const onTabletScheduleId = isSentScheduleActive(tablet.sentSchedule)
    ? tablet.sentSchedule?.scheduleId ?? null
    : null;

  // Editor view
  if (viewMode === 'editor' && selectedScheduleId) {
    return (
      <ScheduleEditor
        scheduleId={selectedScheduleId}
        onBack={handleBackFromEditor}
        isOnTablet={selectedScheduleId === onTabletScheduleId}
      />
    );
  }

  // List view
  return (
    <Box>
      {/* Header; BA-105: wraps when it does not fit (HU at 390 px) */}
      <Box
        data-testid="schedules-header"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
          mb: 3,
        }}
      >
        <Typography variant="h5">{t('schedules.title')}</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<TemplateIcon />}
            disabled
            title="Coming soon"
            sx={headerButtonSx}
          >
            {t('schedules.fromTemplate')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={headerButtonSx}
          >
            {t('schedules.createNew')}
          </Button>
        </Box>
      </Box>

      {/* BA-85: what a send would replace, and the way to the live panel */}
      <OnTabletStrip
        deviceName={deviceName}
        sentSchedule={tablet.sentSchedule}
        currentAction={tablet.currentAction}
        actionName={actionName}
      />

      {canSend && !tablet.online && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('sentSchedule.youOfflineSend')}
        </Alert>
      )}

      {/* Schedule list */}
      <ScheduleList
        onCreateNew={() => setCreateDialogOpen(true)}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
        onToggleActive={handleToggleActive}
        sendNowFor={sendNowFor}
        onTabletScheduleId={onTabletScheduleId}
      />

      {/* Send / replace confirm: the only place a send is written */}
      {flow.dialog}

      <Snackbar
        open={sentSnack}
        autoHideDuration={6000}
        onClose={(_, reason) => { if (reason !== 'clickaway') setSentSnack(false); }}
        message={t('sentSchedule.sentSnack', { device: deviceName })}
        action={(
          <Button size="small" onClick={() => navigate('/dashboard/controls')} sx={{ minHeight: 44, color: 'primary.light' }}>
            {t('sentSchedule.seeLive')}
          </Button>
        )}
      />

      {/* Create dialog */}
      <CreateScheduleDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={handleScheduleCreated}
      />

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t('schedules.deleteConfirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('schedules.deleteConfirm.message', {
              name: scheduleToDelete?.name ?? '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SchedulesPage;
